/* eslint-disable no-undef */
import * as Actions from '@actions/index';
import {
    AccountService,
    DashletDataProcessService,
    DashletSettingsService,
    DashletService,
    NotificationService,
    TimeService
} from '@services/index';
import {
    alarmStatus,
    AlarmAdditionalData,
    AlarmExtended,
    AlarmResponseLogData,
    ColumnTypes,
    UserDetails,
    MenuItem,
    Filters,
    SelectItem,
    CustomerModel
} from '@models/index';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ContextMenu } from 'primeng/contextmenu';
import { DashletAlarmsSummary } from '@models/index';
import { Observable, of, Subject, Subscription, forkJoin, defer, timer, EMPTY } from 'rxjs';
import { retry, map, tap, catchError, take, takeUntil, switchMap, share, expand } from 'rxjs/operators';

import { select, Store } from '@ngrx/store';

import { AppState, selectEntity } from '@reducers/index';

import { CustomerLogo } from '@models/index';
import { SortDirection } from '@angular/material/sort';
import { environment } from '@environments/environment';
import { MatDialog } from '@angular/material/dialog';
import {
    AlarmSuppressionDialog,
    SuppressAlarmDto
} from './alarm-suppression-dialog/alarm-suppression-dialog.component';
import * as CustomersActions from '@actions/customers/customers.actions';

enum detailAlarmStatus {
    resolving = -1,
    resolveFailed,
    resolveSucceed
}
interface Selects {
    label: string;
    value: string;
}

interface AlarmRepeats {
    repeats: number;
    date: Date;
}

class Alarm {
    id!: string;
    displayName!: string;
    administeredId!: string;
    firstOccurred!: Date;
    lastOccurred!: Date | null;
    equipmentId!: string;
    equipment!: string;
    customerId!: string;
    customer!: string;
    severity!: number;
    resolveStatus!: alarmStatus | null;
}

@Component({
    selector: 'app-dashlet-alarms-summary',
    templateUrl: './dashlet-alarms-summary.component.html',
    styleUrls: ['./dashlet-alarms-summary.component.scss']
})
export class DashletAlarmsSummaryComponent implements OnInit, OnDestroy {
    @Input() dashlet: DashletAlarmsSummary;
    @Input() showDetailNavigation: boolean = true;
    @Output() showDetailDrilldown = new EventEmitter();
    @Output() disableNavigation = new EventEmitter();
    @ViewChild('rowMenu') rowMenu: ContextMenu;
    private axpPartnerId: string = 'e098ddd7-0695-443d-9371-69508b58c919';
    public isAdmin: boolean = false;
    public isBP: boolean = false;
    public isDashboardOnlyUser: boolean = true;

    public repeatColumns: ColumnTypes[] = [
        {
            columnDef: 'repeats',
            header: 'Repeats',
            cell: (row: AlarmRepeats) => row.repeats
        },
        {
            columnDef: 'date',
            header: 'Date',
            cell: (row: AlarmRepeats) => this.timeService.convertToUserTimeZone(row.date),
            type: 'date'
        }
    ];

    public additionalColumns: ColumnTypes[] = [
        {
            columnDef: 'key',
            header: 'Key',
            cell: (row: AlarmAdditionalData) => row.key
        },
        {
            columnDef: 'value',
            header: 'Value',
            cell: (row: AlarmAdditionalData) => row.value,
            dataTooltip: (row: AlarmAdditionalData) => row.value,
            matTooltipClass: 'scrollable-tooltip'
        }
    ];

    public responseColumns: ColumnTypes[] = [
        {
            columnDef: 'ruleName',
            header: 'Alarm Rule',
            cell: (row: AlarmResponseLogData) => (row.isRuleDeleted ? row.ruleName + '[Removed]' : row.ruleName)
        },
        {
            columnDef: 'workflowDefinitionName',
            header: 'Workflows Applied',
            cell: (row: AlarmResponseLogData) =>
                row.workflowDefinitionName === null || row.workflowDefinitionName === ''
                    ? 'Not Found'
                    : row.isWorkflowDeleted
                        ? row.workflowDefinitionName + '[Removed]'
                        : row.workflowDefinitionName
        },
        {
            columnDef: 'matchState',
            header: 'Matched',
            cell: (row: AlarmResponseLogData) =>
                row.matchState?.toLowerCase() === this.alarmNotMatchedConst ? false : true
        }
    ];

    public alarmResponseMenu: MenuItem[] = [
        {
            label: 'Open Alarm Rules',
            command: (data?: AlarmResponseLogData) => this.openAlarmRulesPage(),
            disabled: false,
            title: 'Open alarm rules page'
        },
        {
            label: 'Open Workflows',
            command: (data?: AlarmResponseLogData) => this.openWorkflowDefinitionPage(),
            disabled: false,
            title: 'Open workflow defiition page'
        }
    ];

    public columns: ColumnTypes[] = [
        {
            columnDef: 'severity',
            header: '',
            cell: (element: Alarm) => element.severity,
            type: 'alarmSeverity',
            dataTooltip: (element: Alarm) => `Severity ${element.severity}`
        },
        {
            columnDef: 'displayName',
            header: 'Display Name',
            cell: (element: Alarm) => `${element.displayName}`,
            dataTooltip: (element: Alarm) => this.getDisplayNameTitle(element),
            filterType: 'text',
            dataTooltipPosition: 'right'
        },
        {
            columnDef: 'administeredId',
            header: 'Admin ID',
            cell: (element: Alarm) => `${element.administeredId}`,
            dataTooltip: (element: Alarm) => this.getAdministeredIdTitle(element),
            filterType: 'text',
            dataTooltipPosition: 'right'
        },
        {
            columnDef: 'equipment',
            header: 'Equipment',
            cell: (element: Alarm) => `${element.equipment}`,
            dataTooltip: (element: Alarm) => this.getEquipmentTitle(element),
            filterType: 'text'
        },
        {
            columnDef: 'firstOccurred',
            header: 'Activated ',
            cell: (element: Alarm) => this.timeService.convertToUserTimeZone(element.firstOccurred),
            filterType: 'text',
            display: () => this.expandedView
        },
        {
            columnDef: 'customer',
            header: 'Customer ',
            cell: (element: Alarm) => `${element.customer}`,
            dataTooltip: (element: Alarm) => this.getCustomerTitle(element),
            filterType: 'multiselect',
            display: () => this.isHuge && this.userDetails.EntityGroup !== 2
        }
    ];

    private selectedCustomers: CustomerModel[];
    public customerAssociatedData: CustomerModel[];

    userDetails: UserDetails;
    selectedAlarmId: number;
    detailAlarmStatus: detailAlarmStatus;

    repeatsLoading: boolean = true;
    additionalLoading: boolean = true;
    responseLoading: boolean = true;

    openSubcontainer: string;
    detailsAlarm: any;
    detailsAlarmExtended: Observable<AlarmExtended> = new Subject();
    detailsAlarmRepeats: Observable<AlarmRepeats[]> = new Subject();
    detailsAlarmAdditional: Observable<AlarmAdditionalData[]> = new Subject();
    detailsAlarmResponseLog: Observable<AlarmResponseLogData[]> = new Subject();
    detailsIndex: number;
    detailsHasPrev: boolean;
    detailsHasNext: boolean;

    severityRange: number[];
    items: MenuItem[];
    customers: SelectItem[] = [];
    equipments: SelectItem[] = [];
    selectedEquipments: Selects[];
    sortType: string;
    sortTypeRules: string;

    filterCount: number = 0;
    filteredCountSubject = new Subject<number>();
    filterCountsubscription: Subscription;
    filteredAlarmSubject = new Subject<any>();
    filterAlarmsubscription: Subscription;

    loading: boolean;
    alarmsLoading: boolean = true;
    public displayTable: boolean = true;

    displayNameSearch: any;
    administeredIdSearch: any;
    equipmentSearch: any;
    activatedSearch: any;
    timeInterval: any;

    public sortField = 'severity';
    public sortDirection: SortDirection = 'asc';

    public filters: Filters = {};

    private readonly alarmNotMatchedConst: string = 'alarmnotmatched';
    private uiSubscription: Subscription = new Subscription();
    private subscription: Subscription = new Subscription();
    private pollingSubscription: Subscription = new Subscription();
    public destroy$: Subject<null> = new Subject<null>();

    constructor(
        private dashletService: DashletService,
        private accountService: AccountService,
        private dashletDataProcessingService: DashletDataProcessService,
        private cdRef: ChangeDetectorRef,
        private store$: Store<AppState>,
        private notificationService: NotificationService,
        private dialog: MatDialog,
        private snackBarService: NotificationService,
        private timeService: TimeService,
        private dashletSettingsService: DashletSettingsService
    ) {
        this.userDetails = this.accountService.getUserDetails();
    }

    public ngOnInit(): void {
        this.uiSubscription.add(
            this.dashlet.settingsChanged.subscribe(() => {
                this.displayTable = false;
                this.filters = {};
                this.subscription.unsubscribe();
                this.subscription = new Subscription();
                this.cdRef.detectChanges();
                this.updateSelectedCustomers();
                this.dashlet.resetData();
                this.subscribeToData();
                this.displayTable = true;
            })
        );

        this.uiSubscription.add(
            (this.filterCountsubscription = this.filteredCountSubject.subscribe(count => {
                this.filterCount = count;
            }))
        );
        this.uiSubscription.add(
            (this.filterAlarmsubscription = this.filteredAlarmSubject.subscribe(alarms => {
                this.dashlet.alarms = alarms;
            }))
        );
        this.uiSubscription.add(
            this.dashlet.sizeChange.subscribe(() => {
                this.dashlet.updateAlarms();
                this.cdRef.detectChanges();
            })
        );
        this.subscribeToData();
    }

    private fetchLogo(): void {
        if (this.dashlet.customers.length === 1 && this.dashlet.customerIds.length === 1) {
            const customerId = this.dashlet.customerIds[0];
            this.dashletSettingsService.getCustomerDetails(customerId).subscribe(customer => {
                if (customer.partnerId === this.axpPartnerId) {
                    const uniqueId = (customer.customerId + customer.partnerId).toLowerCase();
                    this.fetchPartnerLogo(customer.customerId, uniqueId);
                } else {
                    this.fetchEntityLogo(customerId);
                }
            });
        } else if (this.dashlet.customers.length > 1) {
            const customerId = this.dashlet.customerIds[0];
            this.dashletSettingsService.getCustomerDetails(customerId).subscribe(customer => {
                const uniqueId = (customer.customerId + customer.partnerId).toLowerCase();
                this.fetchPartnerLogo(customer.customerId, uniqueId);
            });
        }
    }

    private fetchPartnerLogo(customerId: string, uniqueId: string): void {
        this.store$.dispatch(Actions.GetBpEntityLogoForCustomer({ entityId: customerId, uniqueId }));
        this.subscription.add(
            this.store$.pipe(select(selectEntity(uniqueId)), takeUntil(this.destroy$)).subscribe(logo => {
                if (logo) {
                    this.dashlet.logo = new CustomerLogo(logo.image, logo.imageType);
                }
            })
        );
    }

    private fetchEntityLogo(customerId: string): void {
        this.store$.dispatch(Actions.GetEntityLogo({ entityId: customerId }));
        this.subscription.add(
            this.store$.pipe(select(selectEntity(customerId)), takeUntil(this.destroy$)).subscribe(logo => {
                if (logo) {
                    this.dashlet.logo = new CustomerLogo(logo.image, logo.imageType);
                }
            })
        );
    }

    private subscribeToData(): void {
        this.severityRange = [0, 10];
        this.pollingSubscription.unsubscribe();
        this.pollingSubscription = new Subscription();
        this.fetchLogo();
        if (this.dashlet.configured && this.dashlet.customerIds) {
            this.dashlet.customerIds.forEach(id => {
                const fetchAlarms$ = () =>
                    this.dashletDataProcessingService.getAlarmsData(id).pipe(catchError(() => of(null)));

                const alarmData$ = defer(() => fetchAlarms$()).pipe(
                    expand(() => timer(60 * 1000).pipe(switchMap(() => fetchAlarms$()))),
                    share(),
                    takeUntil(this.destroy$)
                );
                const subscription: Subscription = alarmData$.subscribe({
                    next: res => {
                        this.alarmsLoading = false;
                        this.dashlet.processAlarmData(id, res);
                        this.cdRef.detectChanges();
                    },
                    error: error => {
                        this.alarmsLoading = false;
                        this.cdRef.detectChanges();
                    }
                });
                this.pollingSubscription.add(subscription);
            });
        }
        let customersData$ = this.store$.select(state => state.customers.userAssociatedCustomers);
        this.subscription.add(
            customersData$.subscribe(result => {
                if (result && result.length) {
                    this.customerAssociatedData = result;
                    this.items = this.buildMenu();
                } else {
                    this.store$.dispatch(CustomersActions.GetUserAssociatedCustomers());
                }
            })
        );
    }

    // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
    public ngAfterContentChecked(): void {
        this.cdRef.detectChanges();
    }

    public ngOnDestroy(): void {
        this.uiSubscription.unsubscribe();
        this.pollingSubscription.unsubscribe();
        this.subscription.unsubscribe();
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    get expandedView() {
        if (this.dashlet) {
            const s = this.dashlet.getSize();
            if (s !== null) return s.id === 2 || s.id === 3;
        }
        return false;
    }

    get isHuge() {
        const s = this.dashlet.getSize();
        if (s !== null) return s.id === 3;
        return false;
    }
    get isSmall() {
        const s = this.dashlet.getSize();
        if (s !== null) return s.id === 0;
        return false;
    }

    get showCustomer() {
        const d = this.userDetails;
        return d.EntityGroup !== 2;
    }

    public tableSize(): number {
        const s = this.dashlet.getSize();
        if (s.id === 0) return 4;
        else if (s.id === 1) return 16;
        else if (s.id === 2) return 16;
        else if (s.id === 3) return 28;
        return 4;
    }

    public getColumnDisplay(): boolean {
        return this.expandedView;
    }

    public getSeverityColor(severity: number): string {
        return this.dashletService.getAlarmSeverityColor(severity);
    }

    public openDetails(alarmId: string) {
        this.openSubcontainer = 'details';
        this.dashlet.displayHeader = false;
        this.detailsIndex = this.dashlet.alarms.map(alarm => alarm.id).indexOf(alarmId);
        this.showDetailDrilldown.emit();
        this.setDetailsAlarm();
    }

    public closeDetails(): void {
        this.openSubcontainer = null;
        this.dashlet.displayHeader = true;
        this.repeatsLoading = true;
        this.additionalLoading = true;
        this.responseLoading = true;
        this.detailsAlarmExtended = new Subject();
        this.detailsAlarmRepeats = new Subject();
        this.detailsAlarmAdditional = new Subject();
        this.detailsAlarmResponseLog = new Subject();
    }

    public detailsPrev(): void {
        if (this.detailsHasPrev) {
            this.detailsIndex--;
            this.setDetailsAlarm();
        }
    }

    public detailsNext(): void {
        if (this.detailsHasNext) {
            this.detailsIndex++;
            this.setDetailsAlarm();
        }
    }

    private setDetailsAlarm(): void {
        this.detailAlarmStatus = null;
        const alarm = (this.detailsAlarm = this.dashlet.alarms[this.detailsIndex]);
        this.detailsHasPrev = this.detailsIndex > 0;
        this.detailsHasNext = this.detailsIndex < this.dashlet.alarms.length - 1;
        //query extra data
        this.detailsAlarmExtended = this.dashletDataProcessingService
            .getAlarmExtended(alarm.id, alarm.firstOccurred, alarm.customerId)
            .pipe(
                retry(3),
                map((a: AlarmExtended) => {
                    if (a) {
                        a.activatedTime = this.timeService.convertToUserTimeZone(a.activatedTime);
                        a.severity = alarm.severity;
                    }
                    return a;
                })
            );
        this.detailsAlarmRepeats = this.dashletDataProcessingService
            .getAlarmRepeats(alarm.id, alarm.firstOccurred, alarm.customerId)
            .pipe(
                retry(3),
                map(repeats => {
                    this.repeatsLoading = false;
                    return repeats.map((repeat, index) => {
                        return { repeats: repeats.length - index, date: repeat };
                    });
                })
            );
        this.detailsAlarmAdditional = this.dashletDataProcessingService
            .getAlarmAdditionalData(alarm.id, alarm.firstOccurred, alarm.customerId)
            .pipe(
                retry(3),
                tap(data => {
                    this.additionalLoading = false;
                    data.splice(
                        data.findIndex((d: any) => {
                            return d.key === 'AdministeredID';
                        }),
                        1
                    );
                    data.splice(
                        data.findIndex((d: any) => {
                            return d.key === 'AlarmDescriptionText';
                        }),
                        1
                    );
                    data.splice(
                        data.findIndex((d: any) => {
                            return d.key === 'AlarmEventName';
                        }),
                        1
                    );
                    data.splice(
                        data.findIndex((d: any) => {
                            return d.key === 'AlarmResolutionText';
                        }),
                        1
                    );
                    let timestamp: number;
                    timestamp = data.findIndex((d: any) => {
                        return d.key === 'Timestamp';
                    });
                    if (timestamp) {
                        data.splice(timestamp, timestamp);
                    }
                })
            );
        this.detailsAlarmResponseLog = this.dashletDataProcessingService
            .getAlarmResponseLog(alarm.id, alarm.customerId)
            .pipe(
                retry(3),
                map((response: AlarmResponseLogData[]) => {
                    this.responseLoading = false;
                    return response.filter(
                        rule => rule.ruleOrder !== -1 && rule.matchState.toLowerCase() !== this.alarmNotMatchedConst
                    );
                })
            );
    }

    public manageAlarm() {
        window.open(
            `${environment.webPortalUrl}EquipAlarms/EquipAlarm/SPIndex?entityId=` + this.detailsAlarm.customerId
        );
    }

    private openAlarmRulesPage() {
        window.open(
            `${environment.webPortalUrl}EquipAlarmRules/EquipAlarmRule/SPIndex?entityId=` + this.detailsAlarm.customerId
        );
    }

    private openWorkflowDefinitionPage() {
        window.open(`${environment.webPortalUrl}Workflows/Manager/SPIndex?entityId=` + this.detailsAlarm.customerId);
    }

    private resolveAlarm(alarm: Alarm): void {
        this.loading = true;
        alarm.resolveStatus = -1;
        this.disableNavigation.emit(false);
        this.subscription.add(
            this.dashletDataProcessingService
                .resolveAlarm(alarm.id, alarm.equipmentId, alarm.customerId)
                .pipe(
                    take(1),
                    tap(() => {
                        alarm.resolveStatus = 1;

                        this.notificationService.notify(
                            'Alarm Resolved',
                            'success',
                            `'${alarm.displayName}' successfully resolved.`
                        );
                        const currentAlarmIndex = this.dashlet.alarms.findIndex(element => element.id === alarm.id);
                        const tempList = this.dashlet.alarms;
                        tempList.splice(currentAlarmIndex, 1);

                        this.dashlet.alarms = [...tempList];

                        this.dashlet.unresolved = this.dashlet.alarms.length;
                        this.disableNavigation.emit(true);

                        this.loading = false;
                    }),
                    catchError(error => {
                        this.notificationService.notify('Failed to Resolve Alarm', 'error', error);
                        this.showAlarmUnresolved(alarm.id);
                        this.disableNavigation.emit(true);
                        return of(error);
                    })
                )
                .subscribe()
        );
    }

    public drilldownResolvedMessageStyle(): string {
        let alarm = this.detailsAlarm;
        if (this.detailAlarmStatus === 0) return this.dashletService.getSeverityColor(2);
        else if (this.detailAlarmStatus === 1) return this.dashletService.getSeverityColor(0);
        else if (this.detailAlarmStatus === -1) {
            return this.dashletService.getSeverityColor(1);
        }
    }

    public drilldownResolvedMessage(): string {
        let alarm = this.detailsAlarm;
        if (this.detailAlarmStatus === 0) return 'Unable to resolve';
        else if (this.detailAlarmStatus === 1) return 'Alarm is resolved';
        else if (this.detailAlarmStatus === -1) {
            return 'Resolving';
        }
    }

    public resolveAlarmInDetails(alarmId: string): void {
        this.detailAlarmStatus = detailAlarmStatus.resolving;

        const alarm = this.dashlet.alarms.find(element => element.id === alarmId);

        this.dashletDataProcessingService
            .resolveAlarm(alarmId, alarm.equipmentId, alarm.customerId)
            .pipe(
                catchError(error => {
                    this.showAlarmUnresolved(alarmId);
                    return of(error);
                })
            )
            .subscribe(response => {
                if (response.status === 204) {
                    alarm.resolveStatus = 1;
                    setTimeout(
                        function () {
                            const currentAlarmIndex = this.dashlet.alarms.findIndex(element => element.id === alarmId);
                            this.dashlet.alarms.splice(currentAlarmIndex, 1);
                            this.dashlet.alarms = [...this.dashlet.alarms];
                            this.dashlet.unresolved = this.dashlet.alarms.length;
                            this.detailAlarmStatus = detailAlarmStatus.resolveSucceed;
                        }.bind(this),
                        3000
                    );
                } else {
                    this.showAlarmUnresolved(alarmId);
                }
            });
    }

    private showAlarmUnresolved(alarmId: string): void {
        let currentAlarmIndex = this.dashlet.alarms.findIndex(element => element.id === alarmId);
        const alarm = this.dashlet.alarms[currentAlarmIndex];
        alarm.resolveStatus = 0;
        this.detailAlarmStatus = detailAlarmStatus.resolveFailed;
        setTimeout(
            function () {
                alarm.resolveStatus = null;
                this.detailAlarmStatus = null;
            }.bind(this),
            5000
        );
    }

    private getDisplayNameTitle(alarm: Alarm): string {
        if (!alarm) return;
        if (alarm.resolveStatus === 0) return 'Unable to resolve alarm';
        else if (alarm.resolveStatus === 1) return 'Alarm is resolved';
        else {
            return 'Severity ' + alarm.severity + ': ' + alarm.displayName;
        }
    }

    private getAdministeredIdTitle(alarm: Alarm): string {
        if (!alarm) return;
        if (alarm.resolveStatus === 0) return 'Unable to resolve alarm';
        else if (alarm.resolveStatus === 1) return 'Alarm is resolved';
        else {
            return alarm.administeredId;
        }
    }

    private getEquipmentTitle(alarm: Alarm): string {
        if (!alarm) return;
        if (alarm.resolveStatus === 0) return 'Unable to resolve alarm';
        else if (alarm.resolveStatus === 1) return 'Alarm is resolved';
        else {
            return alarm.equipment;
        }
    }

    private getCustomerTitle(alarm: Alarm): string {
        if (!alarm) return;
        if (alarm.resolveStatus === 0) return 'Unable to resolve alarm';
        else if (alarm.resolveStatus === 1) return 'Alarm is resolved';
        else {
            return alarm.customer;
        }
    }

    public configureAlarm(): void {
        const url = `${environment.webPortalUrl}AlarmObject/AlarmObject/Index?entityId=${this.detailsAlarm.customerId}&filterAlarmName=${this.detailsAlarm.displayName}`;
        window.open(url);
    }

    private buildMenu(): MenuItem[] {
        this.isAdmin = this.userDetails.EntityGroup === 0 || this.userDetails.RoleDescription === 'Administrator';
        this.isBP =
            (this.userDetails.EntityGroup === 0 || this.userDetails.EntityGroup === 1) &&
            this.userDetails.RoleDescription !== 'DashboardOnly'; //allow for BPs & VOs
        this.isDashboardOnlyUser = this.userDetails.RoleDescription === 'DashboardOnly';

        const items: MenuItem[] = [
            {
                label: 'Resolve',
                command: (row: Alarm) => {
                    this.resolveAlarm(row);
                },
                disabled: false
            },
            {
                label: 'Details',
                command: (row: Alarm) => {
                    //drill down
                    this.openDetails(row.id);
                },
                disabled: false
            },
            {
                label: 'Quick Suppression',
                command: (row: Alarm) => {
                    this.openAlarmSuppressionModal(row);
                },
                disabled: false
            }
        ];
        //admin only options
        if (this.isAdmin) {
            items.push(
                {
                    label: 'Manage all alarms',
                    command: (row: Alarm) => {
                        window.open(
                            environment.webPortalUrl + 'EquipAlarms/EquipAlarm/SPIndex?entityId=' + row.customerId,
                            '_blank'
                        );
                    },
                    disabled: false
                },
                {
                    label: 'Resolve all alarms',
                    command: (row: Alarm) => {
                        window.open(
                            environment.webPortalUrl +
                            'EquipAlarms/EquipAlarm/SPIndex?entityId=' +
                            row.customerId +
                            '&resolveallflag=true',
                            '_blank'
                        );
                    },
                    disabled: false
                }
            );
        }
        if (this.isBP) {
            this.updateSelectedCustomers();
            const newItem = {
                label: 'Open Access Concentrator',
                command: (row: Alarm) => {
                    window.open(
                        environment.webPortalUrl +
                        'AccessConcentrator/AccessConcentrator/EquipmentList?entityId=' +
                        row.customerId +
                        '&equipmentId=' +
                        row.equipmentId,
                        '_blank'
                    );
                },
                disabledCallBack: (row: Alarm) => this.isAccessConcentratorEnabled(row)
            };
            const isItemAlreadyExists = items.some(item => item.label === newItem.label);
            if (!isItemAlreadyExists) {
                items.push(newItem);
            }
        }
        if (this.isBP && this.isAdmin && !this.isDashboardOnlyUser) {
            items.push(
                {
                    label: 'Configure (override alarm)',
                    command: (row: Alarm) => {
                        //drill down
                        window.open(
                            environment.webPortalUrl +
                            'AlarmObject/AlarmObject/Index?entityId=' +
                            row.customerId +
                            '&filterAlarmName=' +
                            row.displayName,
                            '_blank'
                        );
                    },
                    disabled: false
                },
                {
                    label: 'View Suppressed Alarms',
                    command: () => {
                        window.open(
                            `${environment.webPortalUrl}AlarmSuppression?entityId=` + this.userDetails.EntityId
                        );
                    },
                    disabled: false
                }
            );
        }
        if (!this.isDashboardOnlyUser) {
            items.push({
                label: 'Show MS Teams Call Record',
                command: (row: Alarm) => this.redirectToTeamsRecord(row),
                disabledCallBack: (row: Alarm) => {
                    return !this.showGoToCallRecordForTeams(row.displayName);
                }
            });
            items.push({
                label: 'Show Genesys Conversation Record',
                command: (row: Alarm) => {
                    this.goToGenesysConversationRecord(row.id);
                },
                disabledCallBack: (row: Alarm) => {
                    return !this.showGotToCallRecordBtnForGenesys(row.displayName);
                }
            });
        }
        items.push({
            label: 'Show AXP Call Record',
            command: (row: Alarm) => this.redirectToAxpRecord(row),
            disabledCallBack: (row: Alarm) => {
                return !this.showGoToCallRecordForAXP(row.displayName);
            }
        });
        return items;
    }

    public redirectToTeamsRecord(alarm: Alarm): void {
        window.open(
            environment.webPortalUrl +
            'Availability/TeamsCallRecords?customerId=' +
            alarm.customerId +
            '&alarmId=' +
            alarm.id +
            '&equipmentId=' +
            alarm.equipmentId,
            '_blank'
        );
    }

    public redirectToAxpRecord(alarm: Alarm): void {
        window.open(
            environment.webPortalUrl + 'AxpInteractiveVQMData?customerId=' + alarm.customerId + '&alarmId=' + alarm.id,
            '_blank'
        );
    }

    private updateSelectedCustomers(): void {
        if (this.dashlet.customer) {
            this.selectedCustomers = this.customerAssociatedData.filter(
                item => item.customerId === this.dashlet.customer.customerId
            );
        } else {
            this.selectedCustomers = this.customerAssociatedData.filter(item =>
                this.dashlet.customerIds.includes(item.customerId)
            );
        }
    }

    private isAccessConcentratorEnabled(row: Alarm): boolean {
        if (row) {
            let index = this.selectedCustomers.findIndex(item => item.customerId === row.customerId);
            if (index > -1) {
                return (
                    (this.userDetails.EntityGroup === 0 || this.userDetails.EntityGroup === 1) &&
                    this.selectedCustomers[index].disableAccessConcentrator
                );
            }
        }
        return false;
    }

    private openAlarmSuppressionModal(alarm: Alarm): void {
        if (!alarm.customer) {
            const customer = this.customerAssociatedData.find(item => item.customerId === alarm.customerId);
            alarm.customer = customer ? customer.name : 'Unknown';
        }
        const dialogRef = this.dialog.open(AlarmSuppressionDialog, {
            data: alarm,
            disableClose: true,
            width: '850px'
        });

        dialogRef.afterClosed().subscribe(event => {
            if (event.result) {
                /// Perform alarm operations
                this.snackBarService.notify(
                    'Submitting Rule',
                    'info',
                    'Saving Rule and Resolving matching Alarms.',
                    3500
                );

                /// Central API call to perform changes
                this.dashletDataProcessingService
                    .suppressAlarm(event.data as SuppressAlarmDto)
                    .pipe(take(1))
                    .subscribe(
                        (result: string[]) => {
                            if (result.length > 0) {
                                result.forEach(alarmId => {
                                    const currentAlarmIndex = this.dashlet.alarms.findIndex(
                                        alarm => alarm.id === alarmId
                                    );
                                    const tempList = this.dashlet.alarms;
                                    tempList.splice(currentAlarmIndex, 1);
                                    this.dashlet.alarms = [...tempList];
                                });
                                this.dashlet.unresolved = this.dashlet.alarms.length;
                                this.disableNavigation.emit(true);

                                this.snackBarService.notify(
                                    'Success - Alarm Suppressed!',
                                    'success',
                                    'Alarm is now suppressed. Resolved ' + result.length + ' active alarm(s)',
                                    3500
                                );
                            } else {
                                this.snackBarService.notify(
                                    'Success - Alarm Suppressed!',
                                    'success',
                                    'Alarm is now suppressed. Resolved matching active alarm(s)',
                                    3500
                                );
                            }
                        },
                        error => {
                            this.snackBarService.notify(
                                'Internal Server Error',
                                'error',
                                'There was an error setting up the rule or resolving the matching alarms',
                                3500
                            );
                        }
                    );
            }
        });
    }

    public showGoToCallRecordForTeams(alarmName: string): boolean {
        var alarmNames = [
            'MS Teams Fair User Feedback Received',
            'MS Teams Unknown Future Value Failed Session',
            'MS Teams Bad User Feedback Received',
            'MS Teams Good User Feedback Received',
            'MS Teams Call Setup Failed Session',
            'MS Teams Midcall Failed Session',
            'MS Teams notRated User Feedback Received',
            'MS Teams Excellent User Feedback Received',
            'MS Teams Unknown Failed Session',
            'MS Teams Poor User Feedback Received',
            'MS Teams Poor Quality Call'
        ];
        return alarmNames.some(name => alarmName.includes(name));
    }

    public showGoToCallRecordForAXP(alarmName: string): boolean {
        var alarmNames = [
            'Packet Stream Loss or Anomaly',
            'Medium ICE Connectivity Latency',
            'High ICE Connectivity Latency',
            'No Packet Stream Received',
            'No Packet Stream Transmitted',
            'Moderate Round Trip Delay',
            'High Round Trip Delay',
            'Extreme Round Trip Delay',
            'Potential one Way Voice Path',
            'Potential for no Voice Path',
            'Minor Recent Packet Loss',
            'Recent Packet Loss Detected',
            'TURN Tunneled',
            'AXP Poor Quality Stream'
        ];
        return alarmNames.some(name => alarmName.includes(name));
    }

    public showGotToCallRecordBtnForGenesys(alarmName: string): boolean {
        var alarmNames = [
            'Genesys Cloud Short Conversation',
            'Genesys Cloud Poor Quality Conversation',
            'Genesys Cloud Problematic Phone Call (Problematic Voice Interaction)',
            'Genesys Cloud Problematic Phone Call'
        ];
        return alarmNames.some(name => alarmName.includes(name));
    }

    public goToGenesysRecord(): void {
        forkJoin({
            detailsAlarmAdditional: this.detailsAlarmAdditional,
            detailsAlarmExtended: this.detailsAlarmExtended
        }).subscribe({
            next: ({ detailsAlarmAdditional, detailsAlarmExtended }) => {
                const conversationId = detailsAlarmAdditional.find(detail => detail.key === 'ConversationId')?.value;
                const activatedTime = detailsAlarmExtended?.activatedTime;

                if (activatedTime && conversationId) {
                    this.loading = false;
                    const url = `${environment.webPortalUrl}Availability/GenesysCloudConversationRecords?customerId=${this.detailsAlarm.customerId}&alarmId=${this.detailsAlarm.id}&equipmentId=${this.detailsAlarm.equipmentId}&activatedTime=${activatedTime}&conversationId=${conversationId}`;
                    window.open(url, '_blank');
                } else {
                    this.snackBarService.notify('Redirection failed', 'error', 'Error fetching alarm details.', 3500);
                }
            },
            error: err => {
                this.loading = false;
                this.snackBarService.notify('Redirection failed', 'error', 'Error fetching details.', 3500);
            }
        });
    }

    private goToGenesysConversationRecord(alarmId: string): void {
        this.detailsIndex = this.dashlet.alarms.map(alarm => alarm.id).indexOf(alarmId);
        this.loading = true;
        this.setDetailsAlarm();
        this.detailsAlarmAdditional.subscribe(() => {
            this.goToGenesysRecord();
        });
    }
}
